import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Calendar, Clock, LockIcon, Loader2 } from 'lucide-react';
import { useContent } from '@/hooks/useContent';
import { api } from '@/lib/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export function EditContent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);

  const { data: content } = useQuery({
    queryKey: ['content', id],
    queryFn: async () => {
      const response = await api.get(`/content/${id}`);
      return response.data;
    },
  });

  const [title, setTitle] = useState(content?.title || '');
  const [scheduledDate, setScheduledDate] = useState(
    content?.scheduled_at ? format(new Date(content.scheduled_at), 'yyyy-MM-dd') : ''
  );
  const [scheduledTime, setScheduledTime] = useState(
    content?.scheduled_at ? format(new Date(content.scheduled_at), 'HH:mm') : ''
  );

  useEffect(() => {
    if (content) {
      setTitle(content.title || '');
      if (content.scheduled_at) {
        const date = new Date(content.scheduled_at);
        setScheduledDate(format(date, 'yyyy-MM-dd'));
        setScheduledTime(format(date, 'HH:mm'));
      }
    }
  }, [content]);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const scheduled_at = scheduledDate && scheduledTime
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString()
        : null;

      await api.patch(`/content/${id}`, {
        title,
        scheduled_at
      });

      await queryClient.invalidateQueries({ queryKey: ['content'] });
      toast({
        title: "Changes saved",
        description: "Your content has been updated successfully.",
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const scheduledDateTime = scheduledDate && scheduledTime
    ? new Date(`${scheduledDate}T${scheduledTime}`)
    : null;

  if (isLoading) {
    return (
      <div className="container max-w-2xl py-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center space-y-4 min-h-[400px]"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-lg text-muted-foreground">Loading content...</p>
        </motion.div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="container max-w-2xl py-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center space-y-4 min-h-[400px]"
        >
          <p className="text-lg text-muted-foreground">Content not found</p>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-2xl py-10 space-y-8">
        {/* Header */}
        <div className="flex justify-start">
          <h1 className="text-xl font-medium">Edit Content</h1>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-6">
            {/* Title Section */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm text-muted-foreground">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter content title"
                className="w-full"
              />
            </div>

            <Separator className="my-6" />

            {/* Schedule Section */}
            <div className="space-y-4">
              <Label className="text-sm text-muted-foreground">Schedule Upload</Label>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date" className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Date
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="time" className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    Time
                  </Label>
                  <Input
                    id="time"
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>

              {scheduledDateTime && (
                <p className="text-sm text-muted-foreground text-center">
                  Content will be uploaded at {format(scheduledDateTime, 'MMM d, yyyy, h:mm a')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 pt-4">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}